import { create } from "zustand";

/**
 * UI store — layout-level transient state shared across `AppHeader`,
 * `AppLayout`, and `AppSidebar`.
 *
 * Story 3.1 / PR1 scope:
 *  - `drawerOpen`   : mobile drawer (`<Sheet>`) open state at `< 1024px`.
 *  - `sidebarCollapsed` : persistent-sidebar collapse on desktop (`>= 1024px`).
 *
 * `sidebarCollapsed` is persisted to `localStorage` under
 * `ui:sidebar:collapsed`, but only on desktop viewports. Mobile reads/writes
 * are no-ops to avoid leaking a desktop preference into the mobile drawer.
 */

export const SIDEBAR_COLLAPSED_STORAGE_KEY = "ui:sidebar:collapsed";
export const DESKTOP_BREAKPOINT_PX = 1024;

export interface UIStore {
  drawerOpen: boolean;
  setDrawerOpen: (open: boolean) => void;
  toggleDrawer: () => void;
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
}

function isDesktopViewport(): boolean {
  if (typeof window === "undefined") return false;
  if (typeof window.matchMedia === "function") {
    return window.matchMedia(`(min-width: ${DESKTOP_BREAKPOINT_PX}px)`).matches;
  }
  return window.innerWidth >= DESKTOP_BREAKPOINT_PX;
}

function readPersistedCollapsed(): boolean {
  if (typeof window === "undefined") return false;
  if (!isDesktopViewport()) return false;
  try {
    const raw = window.localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY);
    return raw === "1" || raw === "true";
  } catch {
    return false;
  }
}

function writePersistedCollapsed(collapsed: boolean): void {
  if (typeof window === "undefined") return;
  if (!isDesktopViewport()) return;
  try {
    window.localStorage.setItem(
      SIDEBAR_COLLAPSED_STORAGE_KEY,
      collapsed ? "1" : "0",
    );
  } catch {
    // Swallow quota / privacy-mode errors — collapse is a UX nicety, not data.
  }
}

export const useUIStore = create<UIStore>((set, get) => ({
  drawerOpen: false,
  setDrawerOpen: (open) => set({ drawerOpen: open }),
  toggleDrawer: () => set({ drawerOpen: !get().drawerOpen }),
  sidebarCollapsed: readPersistedCollapsed(),
  setSidebarCollapsed: (collapsed) => {
    writePersistedCollapsed(collapsed);
    set({ sidebarCollapsed: collapsed });
  },
}));

/**
 * Test-only helper. Resets the store to its initial state and clears
 * the persisted desktop-collapse flag. Not exported for runtime use.
 */
export const __testing__ = {
  reset(): void {
    if (typeof window !== "undefined") {
      try {
        window.localStorage.removeItem(SIDEBAR_COLLAPSED_STORAGE_KEY);
      } catch {
        // ignore
      }
    }
    useUIStore.setState({
      drawerOpen: false,
      sidebarCollapsed: false,
    });
  },
};
